import { forwardRef, type ReactNode, type Ref, type ButtonHTMLAttributes } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs } from '../_internal/factory.js';
import { Loader } from '../Loader/Loader.js';
import './Button.css';

export type ButtonVariant =
  | 'filled'
  | 'light'
  | 'outline'
  | 'subtle'
  | 'transparent'
  | 'default'
  | 'gradient'
  /** Backwards-compat aliases for the pre-Stage-1 sdk-ui Button. */
  | 'primary'
  | 'ghost';

export interface ButtonProps
  extends BaseProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  variant?: ButtonVariant;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  fullWidth?: boolean;
  loaderPosition?: 'left' | 'right' | 'center';
  children?: ReactNode;
}

/**
 * Filled / light / outline / subtle / transparent / default / gradient button.
 *
 * Animation defaults: `entrance={false}`, `hover='lift'`. Override both via props.
 *
 * Backwards-compatibility: `variant="primary"` ≡ `"filled"`,
 *                          `variant="ghost"`   ≡ `"subtle"`.
 */
export const Button = forwardRef(function Button(
  props: ButtonProps,
  ref: Ref<HTMLButtonElement>
): ReactNode {
  // Pull out everything `useBaseAttrs` consumes (BaseProps + state + animation
  // tokens) so the leftover `htmlRest` is safe to spread onto the <button>
  // element. Without this split, props like `onClick` were silently dropped.
  const {
    variant = 'filled',
    size = 'sm',
    leftSection,
    rightSection,
    fullWidth,
    loaderPosition = 'left',
    loading,
    disabled,
    type = 'button',
    children,
    hover,
    // BaseProps consumed by useBaseAttrs; DO NOT forward to the DOM.
    className: _cn,
    style: _st,
    color: _color,
    surface: _surface,
    radius: _radius,
    shadow: _shadow,
    unstyled: _unstyled,
    active: _active,
    selected: _selected,
    invalid: _invalid,
    density: _density,
    textScale: _textScale,
    entrance: _entrance,
    entranceDelay: _entranceDelay,
    entranceDuration: _entranceDuration,
    entranceEasing: _entranceEasing,
    entranceTrigger: _entranceTrigger,
    entranceOnce: _entranceOnce,
    focusAnim: _focusAnim,
    loop: _loop,
    exit: _exit,
    staggerIndex: _staggerIndex,
    reducedMotion: _reducedMotion,
    onMount: _onMount,
    onUnmount: _onUnmount,
    gradient: _gradient,
    'data-testid': _dataTestId,
    ...htmlRest
  } = props;

  // Back-compat alias map
  const resolvedVariant: ButtonVariant =
    variant === 'primary' ? 'filled' : variant === 'ghost' ? 'subtle' : variant;

  const base = useBaseAttrs('rr-button', { ...props, loading, disabled, hover: hover ?? 'lift' }, [
    `rr-button--${resolvedVariant}`,
    `rr-button--size-${size}`,
    fullWidth && 'rr-button--full-width'
  ]);

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      {...htmlRest}
      {...base.attrs}
      className={base.className}
      style={base.style}
    >
      {loading && loaderPosition === 'left' ? (
        <span className="rr-button__loader rr-button__loader--left"><Loader size="sm" variant="oval" /></span>
      ) : leftSection ? (
        <span className="rr-button__section rr-button__section--left">{leftSection}</span>
      ) : null}

      {loading && loaderPosition === 'center' ? (
        <Loader size="sm" variant="oval" />
      ) : (
        <span className="rr-button__label">{children}</span>
      )}

      {loading && loaderPosition === 'right' ? (
        <span className="rr-button__loader rr-button__loader--right"><Loader size="sm" variant="oval" /></span>
      ) : rightSection ? (
        <span className="rr-button__section rr-button__section--right">{rightSection}</span>
      ) : null}
    </button>
  );
});
